export const PIPELINE_MANUAL_STAGES = ['engaged', 'qualified', 'contribution'] as const;
export const MANUAL_STAGES = ['engaged', 'qualified', 'contribution', 'denied'] as const;
export type ManualStage = (typeof MANUAL_STAGES)[number];

export const STAGE_RANK: Record<string, number> = {
    identified: 0,
    validated: 1,
    warmed: 2,
    engaged: 3,
    qualified: 4,
    contribution: 5,
};

export const STAGE_LABELS_CUSTOM: Record<string, string> = {
    identified: 'Identified',
    validated: 'Validated',
    warmed: 'Warmed',
    engaged: 'Engaged',
    qualified: 'Qualified',
    contribution: 'Contributing',
    dormant: 'Dormant',
    denied: 'Denied',
};

/** Chip colours match the custom-watchlist report mock (dark text on tinted pills). */
export const STAGE_BADGE_STYLE: Record<string, { backgroundColor: string; color: string }> = {
    identified: { backgroundColor: '#EEF0F2', color: '#5F6B7A' },
    validated: { backgroundColor: '#E8F1FF', color: '#1D6FE9' },
    warmed: { backgroundColor: '#FFF1E0', color: '#E67A22' },
    engaged: { backgroundColor: '#E6F3FF', color: '#2B7CD3' },
    qualified: { backgroundColor: '#E6F6EA', color: '#2F7A45' },
    contribution: { backgroundColor: '#2F7A45', color: '#FFFFFF' },
    dormant: { backgroundColor: '#FDECEC', color: '#C0392B' },
    denied: { backgroundColor: '#EEF0F2', color: '#5F6B7A' },
};

export const STAGE_DESCRIPTIONS_CUSTOM: Record<string, string> = {
    identified: 'On this watchlist; contactability not yet established (no verified email or phone).',
    validated: 'On this watchlist and contactable via at least one verified channel.',
    warmed: 'Responding to general campaigns or outreach in the last 12 months — no watchlist-specific engagement yet.',
    engaged: 'Alumnus has responded to a direct, watchlist-specific outreach or interaction.',
    qualified: "Alumnus has expressed clear intent to contribute to this watchlist's goal.",
    contribution: "Alumnus has actively delivered on the watchlist's intended outcome.",
    dormant: 'Alumnus has not responded to any outreach or engagement in 6+ months',
    denied: 'Alumnus has expressed non-willingness to contribute towards this goal.',
};

export const canAdvance = (effective: string | null | undefined, target: string): boolean =>
    STAGE_RANK[effective || ''] != null && STAGE_RANK[target] != null && STAGE_RANK[target] > STAGE_RANK[effective || ''];

export const canSelectStage = ({
    effective,
    persisted,
    target,
}: {
    effective: string | null | undefined;
    persisted: string | null | undefined;
    target: string | null | undefined;
}): boolean => {
    if (!target) return false;
    if (effective === 'denied') return false;
    if (target === 'denied') return true;
    if (!(PIPELINE_MANUAL_STAGES as readonly string[]).includes(target)) return false;
    const baseline =
        effective === 'dormant' ? (STAGE_RANK[persisted as string] != null ? (persisted as string) : 'identified') : effective;
    return canAdvance(baseline, target);
};

export const REMOVE_REASONS = [
    'No longer meets watchlist criteria',
    'Explicit decline',
    'No response after multiple attempts',
    'Role / profile change',
    'Added in error',
    'Other',
] as const;
